import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, Button, IconButton,
  Paper, CircularProgress, Alert, Divider, Collapse,
} from "@mui/material";
import AddIcon           from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon     from "@mui/icons-material/ArrowBack";
import ReceiptLongIcon   from "@mui/icons-material/ReceiptLong";
import Inventory2Icon    from "@mui/icons-material/Inventory2";
import LinkIcon          from "@mui/icons-material/Link";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import api from "../../../services/api";

const C = {
  primary:   "#2563EB",
  primaryBg: "#EFF6FF",
  surface:   "#FFFFFF",
  surfaceAlt:"#F8FAFC",
  border:    "#E2E8F0",
  borderHov: "#CBD5E1",
  text1:     "#0F172A",
  text2:     "#475569",
  text3:     "#94A3B8",
  success:   "#16A34A",
  successBg: "#F0FDF4",
  successBdr:"#BBF7D0",
  danger:    "#DC2626",
  dangerBg:  "#FEF2F2",
  dangerBdr: "#FECACA",
  infoBdr:   "#BFDBFE",
  warn:      "#D97706",
  warnBg:    "#FFFBEB",
  warnBdr:   "#FDE68A",
  urgentBg:  "#FFF7ED",
  urgentBdr: "#FDBA74",
  urgent:    "#EA580C",
};

const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: C.surface,
    fontSize: "0.82rem", color: C.text1,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderHov },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: C.text3 },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

const inputError = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: "#FFF8F8",
    fontSize: "0.82rem", color: C.text1,
    "& fieldset": { borderColor: C.dangerBdr },
    "&:hover fieldset": { borderColor: C.danger },
    "&.Mui-focused fieldset": { borderColor: C.danger, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: C.danger },
  "& .MuiInputLabel-root.Mui-focused": { color: C.danger },
};

function formatRupiah(value) {
  if (!value) return "Rp 0";
  return "Rp " + Number(value).toLocaleString("id-ID");
}

function isValidUrl(str) {
  if (!str) return true; // kosong = opsional, valid
  try { new URL(str); return true; } catch { return false; }
}

const SIFAT_OPTIONS = [
  { value: "biasa",    label: "Biasa",    color: C.text2,  bg: C.surfaceAlt, border: C.border },
  { value: "segera",   label: "Segera",   color: C.warn,   bg: C.warnBg,     border: C.warnBdr },
  { value: "mendesak", label: "Mendesak", color: C.urgent, bg: C.urgentBg,   border: C.urgentBdr },
];

/* ─── Item Row ─── */
function ItemRow({ item, index, total, onChange, onRemove, showErrors }) {
  const subtotal     = Number(item.quantity || 0) * Number(item.price || 0);
  const linkInvalid  = item.link_produk && !isValidUrl(item.link_produk);
  const nameEmpty    = showErrors && !item.item_name;
  const qtyEmpty     = showErrors && !item.quantity;

  // Auto-prefix https:// saat blur jika ada isi tapi tidak pakai http
  const handleLinkBlur = () => {
    const val = item.link_produk?.trim();
    if (val && !val.startsWith("http://") && !val.startsWith("https://")) {
      onChange(index, "link_produk", "https://" + val);
    }
  };

  const linkSx = linkInvalid ? inputError : inputBase;

  return (
    <Box sx={{
      py: 1.5,
      borderBottom: index < total - 1 ? `1px solid ${C.border}` : "none",
    }}>

      {/* ── Desktop layout ── */}
      <Box sx={{
        display: { xs: "none", sm: "grid" },
        gridTemplateColumns: "32px 1fr 70px 110px 110px 150px 32px",
        gap: 1, alignItems: "start",
      }}>
        {/* No */}
        <Box sx={{
          width: 24, height: 24, borderRadius: "6px", mt: "4px",
          backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: C.primary }}>
            {index + 1}
          </Typography>
        </Box>

        {/* Nama Barang */}
        <Box>
          <TextField size="small" placeholder="Nama Barang" required fullWidth
            value={item.item_name}
            onChange={(e) => onChange(index, "item_name", e.target.value)}
            sx={nameEmpty ? inputError : inputBase} />
          {nameEmpty && (
            <Typography sx={{ fontSize: "0.68rem", color: C.danger, mt: 0.4, ml: 0.5 }}>
              Wajib diisi
            </Typography>
          )}
        </Box>

        {/* Jumlah */}
        <Box>
          <TextField size="small" type="number" placeholder="Jml" fullWidth
            value={item.quantity}
            onChange={(e) => onChange(index, "quantity", e.target.value)}
            inputProps={{ min: 1 }}
            sx={qtyEmpty ? inputError : inputBase} />
          {qtyEmpty && (
            <Typography sx={{ fontSize: "0.68rem", color: C.danger, mt: 0.4, ml: 0.5 }}>
              Wajib
            </Typography>
          )}
        </Box>

        {/* Harga Satuan */}
        <TextField size="small" type="number" placeholder="Harga Satuan" fullWidth
          value={item.price || ""}
          onChange={(e) => onChange(index, "price", e.target.value)}
          inputProps={{ min: 0 }}
          sx={inputBase} />

        {/* Keterangan */}
        <TextField size="small" placeholder="Keterangan" fullWidth
          value={item.notes || ""}
          onChange={(e) => onChange(index, "notes", e.target.value)}
          sx={inputBase} />

        {/* Link Produk */}
        <Box>
          <TextField size="small" placeholder="https://..." fullWidth
            value={item.link_produk || ""}
            onChange={(e) => onChange(index, "link_produk", e.target.value)}
            onBlur={handleLinkBlur}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: "flex", alignItems: "center", mr: 0.5 }}>
                  <LinkIcon sx={{ fontSize: 13, color: linkInvalid ? C.danger : C.text3 }} />
                </Box>
              ),
            }}
            sx={linkSx} />
          {linkInvalid && (
            <Typography sx={{ fontSize: "0.68rem", color: C.danger, mt: 0.4, ml: 0.5 }}>
              Gunakan format https://...
            </Typography>
          )}
        </Box>

        {/* Hapus */}
        <IconButton size="small" onClick={() => onRemove(index)} disabled={total === 1}
          sx={{
            width: 26, height: 26, borderRadius: "6px", mt: "4px",
            color: total === 1 ? C.text3 : C.danger,
            "&:hover": { backgroundColor: total === 1 ? "transparent" : C.dangerBg },
          }}>
          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* ── Mobile layout — card style ── */}
      <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", gap: 1 }}>

        {/* Header: nomor + hapus */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{
            width: 24, height: 24, borderRadius: "6px",
            backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: C.primary }}>
              {index + 1}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => onRemove(index)} disabled={total === 1}
            sx={{
              width: 26, height: 26, borderRadius: "6px",
              color: total === 1 ? C.text3 : C.danger,
              "&:hover": { backgroundColor: total === 1 ? "transparent" : C.dangerBg },
            }}>
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* Nama Barang */}
        <Box>
          <TextField fullWidth size="small" placeholder="Nama Barang" required
            value={item.item_name}
            onChange={(e) => onChange(index, "item_name", e.target.value)}
            sx={nameEmpty ? inputError : inputBase} />
          {nameEmpty && (
            <Typography sx={{ fontSize: "0.68rem", color: C.danger, mt: 0.4, ml: 0.5 }}>
              Nama barang wajib diisi
            </Typography>
          )}
        </Box>

        {/* Jumlah + Harga Satuan */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <Box>
            <TextField size="small" type="number" placeholder="Jumlah" fullWidth
              value={item.quantity}
              onChange={(e) => onChange(index, "quantity", e.target.value)}
              inputProps={{ min: 1 }}
              sx={qtyEmpty ? inputError : inputBase} />
            {qtyEmpty && (
              <Typography sx={{ fontSize: "0.68rem", color: C.danger, mt: 0.4, ml: 0.5 }}>
                Wajib diisi
              </Typography>
            )}
          </Box>
          <TextField size="small" type="number" placeholder="Harga Satuan" fullWidth
            value={item.price || ""}
            onChange={(e) => onChange(index, "price", e.target.value)}
            inputProps={{ min: 0 }}
            sx={inputBase} />
        </Box>

        {/* Keterangan */}
        <TextField fullWidth size="small" placeholder="Keterangan"
          value={item.notes || ""}
          onChange={(e) => onChange(index, "notes", e.target.value)}
          sx={inputBase} />

        {/* Link Produk */}
        <Box>
          <TextField fullWidth size="small" placeholder="Link Produk (opsional, contoh: https://tokopedia.com/...)"
            value={item.link_produk || ""}
            onChange={(e) => onChange(index, "link_produk", e.target.value)}
            onBlur={handleLinkBlur}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: "flex", alignItems: "center", mr: 0.5 }}>
                  <LinkIcon sx={{ fontSize: 13, color: linkInvalid ? C.danger : C.text3 }} />
                </Box>
              ),
            }}
            sx={linkSx} />
          {linkInvalid ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <WarningAmberIcon sx={{ fontSize: 12, color: C.danger }} />
              <Typography sx={{ fontSize: "0.68rem", color: C.danger }}>
                Format link tidak valid. Harus diawali <strong>https://</strong>
              </Typography>
            </Box>
          ) : item.link_produk && isValidUrl(item.link_produk) ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 12, color: C.success }} />
              <Typography sx={{ fontSize: "0.68rem", color: C.success }}>
                Link valid
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.67rem", color: C.text3, mt: 0.4, ml: 0.5 }}>
              Opsional · Contoh: https://tokopedia.com/produk
            </Typography>
          )}
        </Box>

        {/* Subtotal per item */}
        {subtotal > 0 && (
          <Typography sx={{ fontSize: "0.72rem", color: C.text2, textAlign: "right" }}>
            Subtotal:{" "}
            <strong style={{ color: C.primary }}>{formatRupiah(subtotal)}</strong>
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ═══ Main ═══ */
export default function CreateProcurement() {
  const navigate = useNavigate();
  const errorRef = useRef(null);

  const [requesterName, setRequesterName] = useState("");
  const [department,    setDepartment]    = useState("");
  const [requestDate,   setRequestDate]   = useState(new Date().toISOString().slice(0, 10));
  const [usedFor,       setUsedFor]       = useState("");
  const [requestType,   setRequestType]   = useState("biasa");
  const [description,   setDescription]   = useState("");
  const [items,         setItems]         = useState([{ item_name: "", quantity: 1, price: "", notes: "", link_produk: "" }]);

  const [pendingStatus, setPendingStatus] = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [fieldErrors,   setFieldErrors]   = useState({});
  const [showItemErr,   setShowItemErr]   = useState(false);
  const [success,       setSuccess]       = useState(false);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  })();

  useEffect(() => {
    (async () => {
      try {
        const statusRes = await api.get("/statuses");
        const statuses  = Array.isArray(statusRes.data) ? statusRes.data : statusRes.data?.data ?? [];
        const pending   = statuses.find(s => s.name.toLowerCase() === "pending");
        if (pending) setPendingStatus(pending.id);
      } catch (err) { console.error(err); }
    })();
  }, []);

  // Scroll ke error saat muncul
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  const handleItemChange = (index, field, value) => {
    const updated = [...items]; updated[index][field] = value; setItems(updated);
    // Clear error saat user mulai mengedit
    if (fieldErrors[field]) setFieldErrors(p => ({ ...p, [field]: null }));
  };

  const addItem    = () => setItems([...items, { item_name: "", quantity: 1, price: "", notes: "", link_produk: "" }]);
  const removeItem = (index) => { if (items.length > 1) setItems(items.filter((_, i) => i !== index)); };

  const totalAmount = items.reduce((s, i) => s + (Number(i.quantity || 0) * Number(i.price || 0)), 0);

  const handleSubmit = async () => {
    setError(null);
    setFieldErrors({});
    setShowItemErr(false);

    // Validasi field utama
    const fe = {};
    if (!requesterName) fe.requesterName = "Nama pemohon wajib diisi.";
    if (!department)    fe.department    = "Departemen / Unit wajib diisi.";
    if (!requestDate)   fe.requestDate   = "Tanggal pengajuan wajib diisi.";

    if (Object.keys(fe).length > 0) {
      setFieldErrors(fe);
      setError("Mohon lengkapi data yang ditandai di bawah ini.");
      return;
    }

    if (!pendingStatus) {
      setError("Konfigurasi sistem bermasalah: status 'pending' tidak ditemukan. Hubungi administrator.");
      return;
    }
    if (!user?.id) {
      setError("Sesi login kamu sudah habis. Silakan login ulang.");
      return;
    }

    // Validasi items
    const itemsInvalid = items.some(i => !i.item_name || !i.quantity);
    if (itemsInvalid) {
      setShowItemErr(true);
      setError("Nama barang dan jumlah wajib diisi untuk semua item di Daftar Barang.");
      return;
    }

    // Validasi link produk
    const badLinkIndex = items.findIndex(i => i.link_produk && !isValidUrl(i.link_produk));
    if (badLinkIndex !== -1) {
      setShowItemErr(true);
      setError(
        `Link produk pada barang #${badLinkIndex + 1} tidak valid. ` +
        `Pastikan diawali dengan https:// (contoh: https://tokopedia.com/...), atau kosongkan saja jika tidak ada.`
      );
      return;
    }

    try {
      setLoading(true);
      await api.post("/procurements", {
        user_id:        user.id,
        status_id:      pendingStatus,
        request_date:   requestDate,
        requester_name: requesterName,
        department:     department,
        used_for:       usedFor     || null,
        request_type:   requestType,
        description:    description || null,
        items: items.map(i => ({
          item_name:   i.item_name,
          quantity:    Number(i.quantity),
          price:       Number(i.price || 0),
          notes:       i.notes       || null,
          link_produk: i.link_produk || null,
        })),
      });
      setSuccess(true);
      setTimeout(() => navigate("/user/procurement"), 2000);
    } catch (err) {
      // Parse error dari Laravel validation
      const respData = err.response?.data;
      if (respData?.errors) {
        // Laravel mengembalikan object errors field-by-field
        const firstError = Object.values(respData.errors)[0];
        const msg = Array.isArray(firstError) ? firstError[0] : firstError;
        setError("Pengajuan gagal: " + msg);
      } else {
        setError(
          respData?.message ||
          respData?.error   ||
          "Terjadi kesalahan saat mengirim pengajuan. Coba lagi dalam beberapa saat."
        );
      }
    } finally { setLoading(false); }
  };

  const activeSifat = SIFAT_OPTIONS.find(s => s.value === requestType);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 960, mx: "auto" }}>

        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{
            border: `1px solid ${C.border}`, borderRadius: "8px", width: 32, height: 32,
            color: C.text2, backgroundColor: C.surface,
            "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg },
          }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.primaryBg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ReceiptLongIcon sx={{ fontSize: 20, color: C.primary }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: C.text1, lineHeight: 1.2 }}>
                Form Pengadaan Barang
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.25 }}>
                Isi data pengajuan sesuai kebutuhan
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Alert Error ── */}
        <div ref={errorRef}>
          <Collapse in={!!error}>
            <Alert
              severity="error"
              onClose={() => setError(null)}
              icon={<WarningAmberIcon fontSize="small" />}
              sx={{
                mb: 2, borderRadius: "10px", fontSize: "0.83rem",
                border: `1px solid ${C.dangerBdr}`,
                backgroundColor: C.dangerBg,
                "& .MuiAlert-message": { lineHeight: 1.6 },
              }}
            >
              <strong>Ada yang perlu diperbaiki</strong>
              <br />
              {error}
            </Alert>
          </Collapse>
        </div>

        {/* ── Alert Sukses ── */}
        <Collapse in={success}>
          <Alert
            severity="success"
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            sx={{
              mb: 2, borderRadius: "10px", fontSize: "0.83rem",
              border: `1px solid ${C.successBdr}`,
              backgroundColor: C.successBg,
            }}
          >
            <strong>Pengajuan berhasil dikirim!</strong>
            <br />
            Kamu akan dialihkan ke halaman daftar pengajuan...
          </Alert>
        </Collapse>

        <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>

          {/* ── Informasi Pemohon ── */}
          <Box sx={{ px: 2.5, py: "11px", borderBottom: `1px solid ${C.border}`, backgroundColor: C.surfaceAlt }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: C.text2,
              textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Informasi Pemohon
            </Typography>
          </Box>

          <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Nama Pemohon + Departemen */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <Box>
                <TextField fullWidth size="small" label="Nama Pemohon" required
                  placeholder="Masukkan nama pemohon..."
                  value={requesterName}
                  onChange={(e) => { setRequesterName(e.target.value); setFieldErrors(p => ({ ...p, requesterName: null })); }}
                  sx={fieldErrors.requesterName ? inputError : inputBase} />
                {fieldErrors.requesterName && (
                  <Typography sx={{ fontSize: "0.68rem", color: C.danger, mt: 0.4, ml: 0.5 }}>
                    {fieldErrors.requesterName}
                  </Typography>
                )}
              </Box>
              <Box>
                <TextField fullWidth size="small" label="Departemen / Unit" required
                  placeholder="Masukkan nama departemen..."
                  value={department}
                  onChange={(e) => { setDepartment(e.target.value); setFieldErrors(p => ({ ...p, department: null })); }}
                  sx={fieldErrors.department ? inputError : inputBase} />
                {fieldErrors.department && (
                  <Typography sx={{ fontSize: "0.68rem", color: C.danger, mt: 0.4, ml: 0.5 }}>
                    {fieldErrors.department}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Digunakan Untuk + Tanggal */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField fullWidth size="small" label="Digunakan Untuk"
                placeholder="Contoh: Kegiatan seminar, operasional lab..."
                value={usedFor} onChange={(e) => setUsedFor(e.target.value)} sx={inputBase} />
              <Box>
                <TextField type="date" fullWidth size="small" label="Tanggal Diajukan"
                  required InputLabelProps={{ shrink: true }}
                  value={requestDate}
                  onChange={(e) => { setRequestDate(e.target.value); setFieldErrors(p => ({ ...p, requestDate: null })); }}
                  sx={fieldErrors.requestDate ? inputError : inputBase} />
                {fieldErrors.requestDate && (
                  <Typography sx={{ fontSize: "0.68rem", color: C.danger, mt: 0.4, ml: 0.5 }}>
                    {fieldErrors.requestDate}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Sifat Permintaan */}
            <Box>
              <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600,
                textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                Sifat Permintaan
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {SIFAT_OPTIONS.map((opt) => (
                  <Box key={opt.value} onClick={() => setRequestType(opt.value)} sx={{
                    display: "flex", alignItems: "center", gap: 0.75,
                    px: 1.75, py: "7px",
                    border: `1.5px solid ${requestType === opt.value ? opt.border : C.border}`,
                    borderRadius: "8px",
                    backgroundColor: requestType === opt.value ? opt.bg : C.surface,
                    cursor: "pointer", transition: "all 0.15s",
                    "&:hover": { borderColor: opt.border, backgroundColor: opt.bg },
                  }}>
                    <Box sx={{
                      width: 14, height: 14, borderRadius: "3px",
                      border: `1.5px solid ${requestType === opt.value ? opt.color : C.border}`,
                      backgroundColor: requestType === opt.value ? opt.color : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      {requestType === opt.value && (
                        <Box component="span" sx={{
                          width: 6, height: 4,
                          borderLeft: "1.5px solid #fff", borderBottom: "1.5px solid #fff",
                          transform: "rotate(-45deg) translateY(-1px)", display: "block",
                        }} />
                      )}
                    </Box>
                    <Typography sx={{
                      fontSize: "0.8rem",
                      fontWeight: requestType === opt.value ? 600 : 400,
                      color: requestType === opt.value ? opt.color : C.text2,
                    }}>
                      {opt.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* ── Daftar Barang ── */}
          <Box sx={{ px: 2.5, py: "11px", borderBottom: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            backgroundColor: C.surfaceAlt }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Inventory2Icon sx={{ fontSize: 15, color: C.primary }} />
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: C.text2,
                textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Daftar Barang
              </Typography>
              <Box sx={{
                px: "7px", height: 18,
                backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
                borderRadius: "5px", display: "inline-flex", alignItems: "center",
              }}>
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: C.primary }}>
                  {items.length} item
                </Typography>
              </Box>
            </Box>
            <Button size="small" variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />}
              onClick={addItem}
              sx={{
                height: 28, borderRadius: "7px", textTransform: "none",
                fontSize: "0.75rem", fontWeight: 600,
                borderColor: C.primary, color: C.primary,
                "&:hover": { backgroundColor: C.primaryBg },
              }}>
              Tambah
            </Button>
          </Box>

          {/* Table Header — hidden on mobile */}
          <Box sx={{
            display: { xs: "none", sm: "grid" },
            gridTemplateColumns: "32px 1fr 70px 110px 110px 150px 32px",
            gap: 1, px: 2.5, py: "8px",
            backgroundColor: "#F1F5F9", borderBottom: `1px solid ${C.border}`,
          }}>
            {["No", "Nama Barang *", "Jml *", "Harga Satuan", "Keterangan", "Link Produk", ""].map((h, i) => (
              <Typography key={i} sx={{ fontSize: "0.68rem", fontWeight: 700, color: C.text3,
                textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {h}
              </Typography>
            ))}
          </Box>

          {/* Info hint link produk */}
          <Box sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center", gap: 0.75,
            px: 2.5, py: "6px",
            backgroundColor: "#F8FAFF",
            borderBottom: `1px solid ${C.border}`,
          }}>
            <LinkIcon sx={{ fontSize: 11, color: C.text3 }} />
            <Typography sx={{ fontSize: "0.67rem", color: C.text3 }}>
              Link produk opsional · Harus diawali <strong>https://</strong> · Contoh: https://tokopedia.com/produk-anda
            </Typography>
          </Box>

          {/* Rows */}
          <Box sx={{ px: 2.5 }}>
            {items.map((item, index) => (
              <ItemRow key={index} item={item} index={index} total={items.length}
                onChange={handleItemChange} onRemove={removeItem} showErrors={showItemErr} />
            ))}
          </Box>

          {/* Total */}
          {totalAmount > 0 && (
            <Box sx={{
              mx: 2.5, mb: 1.5, px: 2, py: 1,
              backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
              borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <Typography sx={{ fontSize: "0.75rem", color: C.text2, fontWeight: 600 }}>
                Total Estimasi
              </Typography>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: C.primary, letterSpacing: "-0.02em" }}>
                {formatRupiah(totalAmount)}
              </Typography>
            </Box>
          )}

          {/* Catatan */}
          <Box sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
            <TextField multiline rows={2} fullWidth size="small"
              label="Catatan Tambahan (opsional)"
              placeholder="Keterangan umum untuk pengajuan ini..."
              value={description} onChange={(e) => setDescription(e.target.value)}
              sx={inputBase} />
          </Box>

          <Divider />

          {/* Footer */}
          <Box sx={{
            px: 2.5, py: "14px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            flexWrap: "wrap", gap: 1,
            backgroundColor: C.surfaceAlt,
          }}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              px: 1.5, py: "5px",
              backgroundColor: activeSifat?.bg,
              border: `1px solid ${activeSifat?.border}`,
              borderRadius: "8px",
            }}>
              <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>Sifat:</Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: activeSifat?.color }}>
                {activeSifat?.label}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={() => navigate(-1)} disabled={loading}
                sx={{
                  borderRadius: "8px", textTransform: "none", height: 34,
                  borderColor: C.border, color: C.text2, fontSize: "0.82rem",
                  "&:hover": { borderColor: C.borderHov, backgroundColor: C.surfaceAlt },
                }}>
                Batal
              </Button>
              <Button variant="contained" onClick={handleSubmit} disabled={loading || success}
                sx={{
                  borderRadius: "8px", textTransform: "none", height: 34, px: 3,
                  backgroundColor: C.primary, fontWeight: 600, fontSize: "0.82rem",
                  boxShadow: "0 1px 3px rgba(37,99,235,0.35)",
                  "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "0 4px 12px rgba(37,99,235,0.4)" },
                }}>
                {loading
                  ? <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={14} sx={{ color: "#fff" }} />
                      <span>Mengirim...</span>
                    </Box>
                  : "Kirim Pengajuan"
                }
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
